import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart3, Calendar, Eye, ArrowRight, Search } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

export default function EventReportsListPage() {
    const navigate = useNavigate();
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchEvents();
    }, []);

    const fetchEvents = async () => {
        try {
            setLoading(true);
            setError('');
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_URL}/events?is_active=true&is_public=true&_t=${Date.now()}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                cache: 'no-cache'
            });
            
            if (response.ok) {
                const data = await response.json();
                setEvents(data);
            } else {
                setError('Failed to fetch events');
            }
        } catch (error) {
            console.error('Error fetching events:', error);
            setError('Failed to fetch events');
        } finally {
            setLoading(false);
        }
    };

    const filteredEvents = events.filter(event => 
        event.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (event.description && event.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (event.slug && event.slug.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-gray-500">Loading events...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6 p-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <BarChart3 size={28} className="text-blue-600" />
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">Event Reports</h1>
                        <p className="text-gray-600 mt-1">View analytics and visitor data for your events</p>
                    </div>
                </div>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                    {error}
                </div>
            )}

            {/* Search */}
            <div className="bg-white rounded-lg shadow p-4">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <input
                        type="text"
                        placeholder="Search events by name, description, or slug..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                </div>
            </div>

            {/* Events List */}
            {filteredEvents.length === 0 ? (
                <div className="bg-white rounded-lg shadow p-12 text-center">
                    <BarChart3 size={48} className="mx-auto text-gray-400 mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        {searchTerm ? 'No events found' : 'No events available'}
                    </h3>
                    <p className="text-gray-600">
                        {searchTerm 
                            ? 'Try adjusting your search terms'
                            : 'Create an event to start tracking visitor data and generating reports'
                        }
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredEvents.map(event => (
                        <div
                            key={event.id}
                            className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow p-6 cursor-pointer border border-gray-200 hover:border-blue-300"
                            onClick={() => navigate(`/events/${event.id}/reports`)}
                        >
                            <div className="flex items-start justify-between mb-4">
                                <div className="flex-1">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                                        {event.name}
                                    </h3>
                                    {event.description && (
                                        <p className="text-sm text-gray-600 line-clamp-2">
                                            {event.description}
                                        </p>
                                    )}
                                </div>
                                <div className="ml-4 flex-shrink-0">
                                    <div className="bg-blue-100 text-blue-600 rounded-full p-2">
                                        <BarChart3 size={20} />
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-2 mb-4">
                                {event.slug && (
                                    <div className="flex items-center gap-2 text-sm text-gray-600">
                                        <Calendar size={16} />
                                        <span className="font-mono text-xs">{event.slug}</span>
                                    </div>
                                )}
                                {event.oem_name && (
                                    <div className="text-sm text-gray-600">
                                        <span className="font-medium">OEM:</span> {event.oem_name}
                                    </div>
                                )}
                                {event.group_name && (
                                    <div className="text-sm text-gray-600">
                                        <span className="font-medium">Group:</span> {event.group_name}
                                    </div>
                                )}
                            </div>

                            <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                                <span className="text-sm text-gray-500">
                                    {event.is_active ? (
                                        <span className="text-green-600 font-medium">Active</span>
                                    ) : (
                                        <span className="text-gray-400">Inactive</span>
                                    )}
                                </span>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(`/events/${event.id}/reports`);
                                    }}
                                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                                >
                                    <Eye size={16} />
                                    View Reports
                                    <ArrowRight size={16} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

